import React, {
	useEffect, useRef, useState,
} from 'react';
import CustomIcon from '../../Common/CustomIcon/CustomIcon';
import PdfPreview from '../../Common/PdfPreview/PdfPreview';
import ChonkyFileBrowser from '../Chonky/ChonkyFileBrowser';
import {
	DOCUMENT_FORMATS,
	DOCUMENT_MAX_UPLOAD_MB,
} from '../Chonky/lib';
import style from '../Library.module.css';
import {
	addObject,
} from 'Vulture/Content';
import {
	vultureENV,
} from 'Vulture/ENV';
import {
	isAdministrativeUser,
} from 'Vulture/Session';
import Img from 'wrapper/Img';
import { s3PublicURL } from 'Vulture/S3.js';

// Pulls a display file name from a library URL/key — the last path segment,
// query stripped and percent-decoded. Used when file.fileName isn't set (it's
// only populated right after a direct upload, not for existing/library files).
const basenameFromUrl = (url) => {
	if (!url) return '';
	// Decode FIRST: library URLs encode the whole key via encodeURIComponent
	// (getFilePath), so '/' becomes %2F — splitting before decoding would return
	// the entire path. Decode, drop any query, then take the last path segment.
	let decoded = url;
	try {
		decoded = decodeURIComponent(url);
	} catch (e) {
		// malformed encoding — fall back to the raw string
	}
	return decoded.split('?')[0].split('/').pop();
};

export const BaseUploader = (props) => {
	const buttonRef = useRef();
	const copyTimeoutRef = useRef(null);
	const [ urlCopied, setUrlCopied, ] = useState(false);

	// Clear the "Copied" reset timer on unmount so it never calls setState on an
	// unmounted component.
	useEffect(() => {
		return () => {
			clearTimeout(copyTimeoutRef.current);
		};
	}, []);

	const {
		app,
		accountUUID,
		handleImageUpload,
		deleteContent,
		openLibraryModal,
		label,
		s3Key,
		entityName,
		fileSizeLimit,
		fileSizeUnit,
		uploadedImgXResolution,
		uploadedImgYResolution,
		uploadedImgUnit,
		file,
		type,
	} = props;

	// Full library URL of the current file (set on mount and on every upload/select).
	const fileUrl = file?.previewSrc || '';
	const isPdfType = type === 'PDF';
	const hasFile = Boolean(s3Key);
	const fileName = file?.fileName || basenameFromUrl(fileUrl);

	// Clicks the actual image upload button
	const clickImageUpload = () => {
		let imageUploadButton = buttonRef.current;
		imageUploadButton.click();
	};

	// Opens the current PDF in an in-app modal (iframe embed). PDFs have no
	// thumbnail, so the icon doubles as the click target for previewing.
	// `omitClose` + a custom Close button are deliberate: the shared modal X
	// calls app.closeModal(), which resets pendingChanges and would discard an
	// unsaved upload. closeModalAndKeepPendingChanges() preserves it — the
	// preview is read-only and must not touch form state.
	const openPdfPreview = () => {
		if (!fileUrl) return;
		app.openModal({
			modalSize:  3,
			showModal:  true,
			modalProps: {
				// customClass removes the modal body's default scrollbars — the
				// iframe provides its own (see .pdf-preview-content in App.css).
				customClass: 'pdf-preview-content',
				// Title renders in the modal's black header bar; put the close X
				// on its right. omitClose hides the native X (which calls
				// app.closeModal() and would wipe unsaved form changes); our X
				// uses closeModalAndKeepPendingChanges so the preview is read-only.
				title:       (
					<div style={{
						display:        'flex',
						alignItems:     'center',
						justifyContent: 'space-between',
						width:          '100%',
					}}>
						<span>{`Preview: ${label || 'PDF'}`}</span>
						<button
							type="button"
							aria-label="Close"
							onClick={(e) => {
								e.preventDefault();
								app.closeModalAndKeepPendingChanges();
							}}
							style={{
								cursor:     'pointer',
								display:    'inline-flex',
								background: 'none',
								border:     'none',
								padding:    0,
								color:      'inherit',
							}}
							title="Close"
						>
							<CustomIcon icon='times' />
						</button>
					</div>
				),
				omitClose: true,
				jsx:       (
					<div style={{
						width:    '100%',
						height:   '80vh',
						overflow: 'hidden',
					}}>
						<PdfPreview fileUrl={fileUrl} accountUUID={accountUUID} label={label} />
					</div>
				),
			},
		});
	};

	const copyPdfUrl = (e) => {
		e.preventDefault();
		// No URL to copy (e.g. mid replace-upload, when previewSrc is briefly null).
		if (!fileUrl) return;
		// navigator.clipboard is undefined in insecure contexts / older browsers;
		// accessing .writeText would throw synchronously (bypassing the .catch),
		// so guard first and no-op when the Clipboard API is unavailable.
		if (!navigator.clipboard || !navigator.clipboard.writeText) return;
		navigator.clipboard.writeText(fileUrl).then(() => {
			setUrlCopied(true);
			clearTimeout(copyTimeoutRef.current);
			copyTimeoutRef.current = setTimeout(() => {
				setUrlCopied(false);
			}, 2000);
		}).catch(() => {
			// Write denied/failed — leave the button label unchanged.
		});
	};

	let accepted = '.jpg, .jpeg, .png';

	if (type === 'PDF') {
		accepted = '.pdf';
	} else if (type === 'Video') {
		accepted = '.mp4, .webm';
	}
	let fileFormat = `File format: ${accepted.toUpperCase()}`;

	let deleteButton = 'button';

	const hasNoFile = s3Key === '';

	if (hasNoFile) {
		deleteButton = 'button button-disabled no-click';
	}

	// Icon tooltip is PDF-only: the PDF icon is the view trigger, so it reflects
	// whether there's a file to view. Image/Video icons open the file dialog, so
	// they get no tooltip.
	let pdfIconTitle;
	if (isPdfType) {
		pdfIconTitle = fileUrl ? 'Click to view' : 'No file to view';
	}

	// Copy URL (PDF, admin only): an outlined blue-on-white button when a file is
	// present; greyed and non-interactive like Delete when there's no file.
	const copyUrlButton = fileUrl ? 'button-copy-url' : 'button button-disabled no-click';

	// Helper text shown as a tooltip on the label info icon (all uploader types).
	// PDFs have no resolution; images/videos keep their max-resolution line.
	const tooltipText = isPdfType ?
		`Click SAVE to submit your changes.\n${fileFormat} (Max: ${DOCUMENT_MAX_UPLOAD_MB}MB)` :
		`Save the form to submit your changes.\n${fileFormat} (Max: ${fileSizeLimit}${fileSizeUnit})\nMax Resolution: ${uploadedImgXResolution}${uploadedImgUnit} x ${uploadedImgYResolution}${uploadedImgUnit}`;

	return (
		<div className="uploaderWrapper">
			<div className="label">
				<label>{label || ''}</label>
				<span className="uploader-info" title={tooltipText}>
					<CustomIcon icon='info-circle' />
				</span>
			</div>
			<div className="uploader">
				<div className="uploader-media">
					<div
						onClick={isPdfType ? openPdfPreview : clickImageUpload}
						className="uploader-image"
						data-test-id="uploaderImage"
						style={isPdfType ? {
							cursor: fileUrl ? 'pointer' : 'default',
						} : undefined}
						title={pdfIconTitle}
					>
						{type === 'PDF' && (s3Key ? <CustomIcon icon='file-pdf' color='#00baff' size='5x'></CustomIcon> : <CustomIcon icon="file" size='5x' />)}
						{(type === 'Image' || type === 'Unknown') && (s3Key ? <Img src={file.previewSrc} alt={file.fileName || entityName || 'Upload preview'} /> : <CustomIcon icon="image" />)}
						{type === 'Video' && (s3Key ? <CustomIcon icon='video' color='#00baff' size='5x'></CustomIcon> : <CustomIcon icon="video" size='5x' />)}
					</div>
					{hasFile && fileName && (
						<div className="uploader-filename" title={fileName}>{fileName}</div>
					)}
				</div>
				<div className="uploader-actions">
					<div className="uploader-buttons">
						<button
							onClick={clickImageUpload}
							className="button"
						>
							<CustomIcon icon='cloud-upload-alt' />
							{hasNoFile ? 'Upload' : 'Replace'}
						</button>
						<button
							onClick={(e) => {
								e.preventDefault();
								openLibraryModal();
							}}
							className="button"
						>
							<CustomIcon icon='book' />Library
						</button>
						<button
							onClick={(e) => {
								e.preventDefault();
								deleteContent(file);
							}}
							className={deleteButton}
						>
							<CustomIcon icon='trash' />Delete
						</button>
						{isPdfType && isAdministrativeUser() && (
							<button className={copyUrlButton} onClick={copyPdfUrl}>
								<CustomIcon icon='copy' />{urlCopied ? 'Copied' : 'Copy URL'}
							</button>
						)}
						<input
							id={`${entityName}Button`}
							data-test-id="uploaderFileInput"
							onChange={(e) => {
								handleImageUpload(e);
							}}
							accept={accepted}
							ref={buttonRef}
							type='file'
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

const ImageUploaderComponent = (props) => {
	const {
		app,
		accountUUID,
		component: {
			settings,
		},
		label,
		value,
		attr,
	} = props;

	const {
		// source: imgSrc = '',
		file_size_limit: fileSizeLimit,
		file_size_unit: fileSizeUnit,
		uploaded_img_x_resolution: uploadedImgXResolution,
		uploaded_img_y_resolution: uploadedImgYResolution,
		uploaded_img_unit: uploadedImgUnit,
		entityName,
	} = settings;

	let {
		type,
	} = props;

	if (!type) {
		switch (label) {
		case 'ImageComponent':
			type = 'Image';
			break;
		case 'PdfComponent':
			type = 'PDF';
			break;
		case 'VideoComponent':
			type = 'Video';
			break;
		case undefined:
		case null:
			type = 'Unknown';
			break;
		default:
			type = 'Unknown';
			break;
		}
	}

	const imgSrc = value || '';

	const [ file, setFile, ] = useState({});
	const [ s3Key, setS3Key, ] = useState(imgSrc);

	useEffect(() => {
		let parsedSrc = imgSrc;
		// Get initial AWS Key from URL
		let split = imgSrc.split('.com/');
		if (split[1]) {
			parsedSrc = split[1];
			parsedSrc = decodeURI(parsedSrc);
		}
		setS3Key(parsedSrc);
		setFile({
			previewSrc: imgSrc,
		});
	}, []);

	const handleImageUpload = (e) => {
		e.preventDefault();
		if (e?.target?.files?.length > 0) {
			let file = e.target.files[0];

			let fileType = file.type;
			let fileArray = fileType.split('/');
			let columnType = fileArray[0] === 'application' ? fileArray[1] : fileArray[0];

			// Only images use the inline data-URL preview. PDFs/videos show an icon
			// (and get their library URL after upload), so skip FileReader for them —
			// readAsDataURL loads the whole file into memory and would freeze/crash
			// the browser on large PDFs (50MB) or videos (400MB).
			if (columnType === 'image') {
				const reader = new FileReader();
				reader.onload = (ev) => {
					setFile({
						file,
						fileName:   file.name,
						fileType:   columnType,
						previewSrc: ev.target.result,
					});
				};
				reader.onerror = () => {
					alert(`Error occurred while loading ${columnType}`);
				};
				reader.readAsDataURL(file);
			} else {
				setFile({
					file,
					fileName:   file.name,
					fileType:   columnType,
					previewSrc: null,
				});
			}
			uploadContent(file);
		}
	};

	const uploadContent = (file) => {
		addObject(file, file.name, accountUUID)
			.then((path) => {
				const fullImgSrc = s3PublicURL('ts-condor-assets', `${vultureENV}/ts-library/${path}`);
				setValue(fullImgSrc, false);
			})
			.catch((err) => {
				console.error('Uploader.uploadContent failed:', err);
			});
	};

	const deleteContent = (file) => {
		setS3Key('');
		setValue('', false);
	};

	const setValue = (val) => {
		setS3Key(val);
		setFile({
			previewSrc: val,
		});
		props.setValue(val);
		app.closeLibraryModal();
	};

	const openLibraryModal = () => {
		app.openModal({
			showModal:  true,
			modalSize:  2,
			library:    true,
			modalProps: {
				title:       'Library',
				customClass: 'test',
				type,
				jsx:         (
					<LibraryModal
						app={app}
						accountUUID={accountUUID}
						setValue={setValue}
						type={type}
					/>
				),
			},
		});
	};

	return (
		<BaseUploader
			app={app}
			accountUUID={accountUUID}
			handleImageUpload={handleImageUpload}
			deleteContent={deleteContent}
			openLibraryModal={openLibraryModal}
			label={label || attr.label || ''}
			type={type}
			imgSrc={imgSrc}
			s3Key={s3Key}
			entityName={entityName}
			fileSizeLimit={fileSizeLimit}
			fileSizeUnit={fileSizeUnit}
			uploadedImgXResolution={uploadedImgXResolution}
			uploadedImgYResolution={uploadedImgYResolution}
			uploadedImgUnit={uploadedImgUnit}
			file={file}
		/>
	);
};

class LibraryModal extends React.Component {
	constructor(props) {
		super(props);
	}

	render() {
		const {
			app,
			accountUUID,
			setValue,
			type,
		} = this.props;

		return (
			<div className={style.chonkyWrapper}>
				<ChonkyFileBrowser
					app={app}
					mode={type === 'PDF' ? 'select-pdf' : 'select-image'}
					readOnly={true}
					allowedFormats={type === 'PDF' ? DOCUMENT_FORMATS : undefined}
					onSelectImage={(image) => {
						// setValue sets previewSrc to the real file URL (imgURL) for both images
						// and PDFs; do not pre-set it to previewURL (a .webp thumbnail that 404s for PDFs).
						setValue(image.imgURL);
					}}
					accountUUID={accountUUID}
				/>
			</div>
		);
	}
}

export default ImageUploaderComponent;
